<script setup>
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { getSettingsNavGroups } from './settingsCatalog';
import {
  isWorkspaceV2Path,
  workspaceV2Path,
} from 'dashboard/helper/workspaceV2';
import { worldById } from 'dashboard/routes/dashboard/hub/hubWorldLookup';
import { settingsCenterTarget, settingsDestination } from './settingsContext';

// 'sidebar' e a coluna do computador; 'page' e a lista em tela cheia do
// celular, onde o item precisa de alvo maior e da seta que diz "isto abre".
const props = defineProps({
  variant: {
    type: String,
    default: 'sidebar',
    validator: valor => ['sidebar', 'page'].includes(valor),
  },
});

const emTela = computed(() => props.variant === 'page');

const { t } = useI18n();
const { isAdmin } = useAdmin();
const { isFeatureFlagEnabled, checkInstallationType, checkPermissions } =
  usePolicy();
const currentAccountId = useMapGetter('getCurrentAccountId');
const route = useRoute();
const router = useRouter();

const busca = ref('');

const grupos = computed(() =>
  getSettingsNavGroups({
    isAdmin: isAdmin.value,
    isFeatureEnabled: isFeatureFlagEnabled,
    checkInstallationType,
    checkPermissions,
    translate: t,
    query: busca.value,
    router,
    params: route.params,
  })
);

const semResultado = computed(() => busca.value.trim() && !grupos.value.length);

const centerTarget = computed(() => settingsCenterTarget(route));
const itemRoute = item => {
  if (route.query.config_center === '1')
    return settingsDestination(router, route, {
      ...item,
      scope: item.id === 'profile' ? 'personal' : 'account',
    });
  const target = {
    name: item.routeName,
    params: { accountId: currentAccountId.value },
  };
  if (!isWorkspaceV2Path(route.path)) return target;

  const resolved = router.resolve(target);
  const fromWorld = route.query?.from_world;
  const fromWorldId =
    typeof fromWorld === 'string' ? worldById(fromWorld)?.id : undefined;

  return {
    path: workspaceV2Path(resolved.path),
    ...(fromWorldId ? { query: { from_world: fromWorldId } } : {}),
  };
};
</script>

<template>
  <nav
    class="flex-col gap-5 overflow-y-auto"
    :class="
      emTela
        ? 'flex w-full h-full px-4 pt-4 pb-24 bg-n-surface-1'
        : 'hidden w-64 px-3 py-4 border-r shrink-0 md:flex border-n-weak bg-n-solid-1'
    "
    :aria-label="$t('SETTINGS_NAV.TITLE')"
  >
    <RouterLink
      :to="centerTarget"
      class="flex min-h-11 items-center gap-2 px-2 text-sm font-semibold text-n-blue-11"
    >
      <span class="i-ph-squares-four size-4" />
      {{ $t('SETTINGS_CENTER.ALL_SETTINGS') }}
    </RouterLink>
    <div class="flex flex-col gap-1 px-2">
      <span
        class="font-medium tracking-tight text-n-slate-12"
        :class="emTela ? 'text-xl' : 'text-base'"
      >
        {{ $t('SETTINGS_NAV.TITLE') }}
      </span>
    </div>

    <label class="relative flex items-center mx-1">
      <span class="sr-only">{{ $t('SETTINGS_NAV.SEARCH') }}</span>
      <span
        class="absolute pointer-events-none i-ph-magnifying-glass size-3.5 text-n-slate-10 top-1/2 -translate-y-1/2 ltr:left-2.5 rtl:right-2.5"
      />
      <input
        v-model="busca"
        type="search"
        class="!m-0 !h-11 w-full py-2 !text-base border rounded-xl bg-n-background border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand ltr:!pl-10 ltr:!pr-2 rtl:!pr-10 rtl:!pl-2"
        :placeholder="$t('SETTINGS_NAV.SEARCH')"
      />
    </label>

    <p v-if="semResultado" role="status" class="px-2 text-xs text-n-slate-11">
      {{ $t('SETTINGS_NAV.NO_RESULTS', { query: busca }) }}
    </p>

    <div
      v-for="grupo in grupos"
      :key="grupo.id"
      class="flex flex-col"
      :class="emTela ? 'gap-1.5' : 'gap-0.5'"
    >
      <span
        class="px-2.5 pt-1 pb-1 text-[10px] font-medium tracking-wider uppercase text-n-slate-10"
      >
        <!-- As chaves vem do registro em settingsNavGroups.js, que e a fonte
             unica do menu; sao literais conhecidas, nao entrada do usuario. -->
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
        {{ t(grupo.labelKey) }}
      </span>
      <router-link
        v-for="item in grupo.items"
        :key="item.id"
        :to="itemRoute(item)"
        :active-class="
          emTela ? '' : 'bg-n-alpha-2 !text-n-slate-12 font-semibold'
        "
        class="flex items-center gap-2.5 leading-tight rounded-lg text-n-slate-11 hover:bg-n-alpha-1"
        :class="
          emTela
            ? 'px-3 min-h-11 text-sm bg-n-solid-1 border border-n-weak text-n-slate-12'
            : 'px-2.5 py-1.5 text-[13px]'
        "
      >
        <span :class="item.icon" class="shrink-0 size-4" />
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
        {{ t(item.labelKey) }}
        <span
          v-if="emTela"
          class="i-ph-caret-right size-4 text-n-slate-10 ltr:ml-auto rtl:mr-auto rtl:rotate-180"
        />
      </router-link>
    </div>
  </nav>
</template>
